import React, { PropsWithChildren } from 'react';
import {
  init,
  ErrorBoundary as ErrorBoundaryComponent,
  browserTracingIntegration,
} from '@sentry/react';
import { ButtonLink } from '@/ui-kit/button-link';
import { useTimezoneContext } from '@/features/timezone';
import { useLanguageContext } from '@/features/language';
import { storage, StorageKey } from '@/services/storage';
import { DateVo } from '@/utils/value-objects';
import { APP_ENV } from '@/constants/env';
import { AppEnv } from '@/entities';
import FailIcon from '~/icons/fail-icon.svg';
import { useLocalizedTexts } from './localization';
import {
  ErrorBoundaryContainer,
  ErrorBoundaryContentWrapper,
  ErrorBoundaryContent,
  ErrorBoundaryTitle,
  ErrorBoundaryWarning,
} from './styles';

init({
  dsn: 'https://d20f9ea521ef9723ee75b1afe88021c2@o542333.ingest.us.sentry.io/4506020161191937',
  integrations: [browserTracingIntegration()],
  beforeSend: (event) => {
    if (window.location.hostname === 'localhost') {
      console.log(JSON.stringify(event));
      return null;
    }

    return event;
  },
});

const FallbackView = () => {
  const l = useLocalizedTexts();

  return (
    <ErrorBoundaryContainer>
      <ErrorBoundaryContentWrapper>
        <ErrorBoundaryContent>
          <FailIcon />
          <ErrorBoundaryTitle>{l.ohSorryGotSomeTroublesText}</ErrorBoundaryTitle>
          <ErrorBoundaryWarning>
            <span>{l.pleaseReloadThePageText}</span>
            <span>
              <span>{l.ifThisDoesNotHelpPleaseClearTheCacheText}</span>
              {' '}
              <ButtonLink
                as="a"
                href="https://workee.net/blog/boost-work-efficiency-clear-cache"
                target="_blank"
              >
                {l.howToCleanCacheText}
              </ButtonLink>
            </span>
          </ErrorBoundaryWarning>
        </ErrorBoundaryContent>
      </ErrorBoundaryContentWrapper>
    </ErrorBoundaryContainer>
  );
};

export const ErrorBoundary = ({ children }: PropsWithChildren) => {
  const { timezone: currentTimezone } = useTimezoneContext();
  const { language: currentLanguage } = useLanguageContext();

  const performAutoReload = () => {
    storage.setItem(StorageKey.LAST_ON_ERROR_AUTO_RELOADED_AT, DateVo.now({
      timezone: currentTimezone,
      language: currentLanguage,
    }).iso);
    window.location.reload();
  };

  const handleError = () => {
    try {
      const lastOnErrorAutoReloadedAt = storage.getItem(StorageKey.LAST_ON_ERROR_AUTO_RELOADED_AT);
      if (!lastOnErrorAutoReloadedAt) {
        performAutoReload();
        return;
      }

      if (DateVo.now({
        timezone: currentTimezone,
        language: currentLanguage,
      }).subtract({ minutes: 1 }).gt(new DateVo({
        date: lastOnErrorAutoReloadedAt,
        timezone: currentTimezone,
        language: currentLanguage,
      }))) {
        performAutoReload();
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <ErrorBoundaryComponent
      onError={APP_ENV === AppEnv.PRODUCTION ? handleError : undefined}
      fallback={<FallbackView />}
    >
      {children}
    </ErrorBoundaryComponent>
  );
};
